import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { VStack } from "@chakra-ui/react";
import { Button, Card, DatePicker, Input, List, Space } from "antd";
import Paragraph from "antd/lib/typography/Paragraph";
import useWebsiteSettings from "hooks/useWebsiteSettings";
import { DateOnlyFormat, DisplayDateFormat } from "models/websiteSettings";
import moment from "moment-timezone";
import { ReactNode, useState } from "react";
import SettingsHeader from "./SettingsHeader";

interface DateManagementCardProps {
    title: string;
    description: ReactNode;
    addDate: (date: string) => void;
    removeDate: (date: string) => void;
    data: string[];
    loading?: boolean
}

const DateManagementCard = ({
    title,
    description,
    addDate,
    removeDate,
    data,
    loading = false
}: DateManagementCardProps) => {
    const [newDate, setNewDate] = useState<moment.Moment | null>(null);

    const add = () => {
        addDate(newDate!.format(DateOnlyFormat));
        setNewDate(null);
    }

    const remove = (date: moment.Moment) => {
        removeDate(date.format(DateOnlyFormat));
    }

    return <Card title={title} size="small" type="inner">
        <Paragraph>
            {description}
        </Paragraph>
        <VStack display='flex' alignItems='start'>
            <Input.Group compact>
                <DatePicker
                    format={DisplayDateFormat}
                    disabledDate={(date: moment.Moment) => date < moment().endOf('day')}
                    showToday={false}
                    onChange={setNewDate}
                    value={newDate}
                    disabled={loading}
                />
                <Button icon={<PlusOutlined />} type="primary" disabled={newDate === null || loading} onClick={add} />
            </Input.Group>
            <List
                style={{ width: '174px' }}
                loading={loading}
                size="small"
                bordered
                dataSource={data}
                pagination={false}
                renderItem={item => <List.Item
                    actions={[<Button icon={<DeleteOutlined />}
                        disabled={loading}
                        size="small"
                        danger
                        type="link"
                        onClick={() => remove(moment(item))} />]}
                >{moment(item).format(DisplayDateFormat)}</List.Item>}
            />
        </VStack>
    </Card>;
}

const DateManagementSection = () => {
    const {
        isLoading,
        settings,
        outOfOffice: {
            add: addOOO,
            remove: removeOOO
        },
        auctionEndBlacklist: {
            add: addAEB,
            remove: removeAEB
        },
    } = useWebsiteSettings();

    const {
        outOfOfficeDates = [],
        auctionEndBlacklistDates = []
    } = settings || {};

    return <>
        <SettingsHeader title="Date Management" />
        <Paragraph>
            Old dates will automatically fall off of both of these lists.
        </Paragraph>
        <Space size={30}>
            <DateManagementCard
                title="Out of Office Dates"
                description={<>
                    These dates will be taken into account when determining the estimated upload date<br />
                    for a package, along with the configured wait times.
                </>}
                data={outOfOfficeDates}
                addDate={addOOO}
                removeDate={removeOOO}
                loading={isLoading}
            />

            <DateManagementCard
                title="Auction End Blacklist Dates"
                description={<>
                    These dates will be taken into account when creating new listings on eBay. If a listing<br />
                    would end on one of these days, the listing will be extended by 2 to 3 days.<br />
                </>}
                data={auctionEndBlacklistDates}
                addDate={addAEB}
                removeDate={removeAEB}
                loading={isLoading}
            />
        </Space>
    </>;
}

export default DateManagementSection;